import { Box } from "@mui/material";
import { useMemo } from "react";
import type { Span } from "../../api/client";
import type { TimeRange } from "../../utils/time";
import { getSpanColor } from "../../utils/colors";

interface SpanTimelineProps {
  spans: Span[];
  timeRange: TimeRange;
  hoveredSpanId: string | null;
  onSpanClick: (spanId: string) => void;
  onSpanHover: (spanId: string | null) => void;
}

export function SpanTimeline({
  spans,
  timeRange,
  hoveredSpanId,
  onSpanClick,
  onSpanHover,
}: SpanTimelineProps) {
  // Calculate time range
  const timeSpan = timeRange.to.getTime() - timeRange.from.getTime();
  const minBarWidth = 2; // Minimum width for very short spans

  // Calculate positions and assign rows for overlapping spans
  const { spanPositions, numRows } = useMemo(() => {
    // Small offset to ensure edge spans are visible (as percentage)
    const edgeOffset = 1; // 1% offset from edges to ensure visibility

    // First, calculate basic positions for all spans
    const spansWithPositions = spans.map((span) => {
      const startTime = new Date(span.start_time).getTime();
      const endTime = new Date(span.end_time).getTime();
      const duration = endTime - startTime;

      const relativeStart = startTime - timeRange.from.getTime();
      // Calculate position as percentage, with edge offset
      const rawXPercent = (relativeStart / timeSpan) * 100;
      // Clamp and add edge offset for visibility
      const xPercent = Math.max(edgeOffset, Math.min(100 - edgeOffset, rawXPercent));

      const rawWidthPercent = (duration / timeSpan) * 100;
      // Ensure minimum width, but also ensure it doesn't extend beyond right edge
      const maxWidth = 100 - xPercent - edgeOffset;
      const widthPercent = Math.max(
        minBarWidth / (timeSpan / 100),
        Math.min(rawWidthPercent, maxWidth),
      );

      const isTooSmall = widthPercent < 0.5;
      const color = getSpanColor(span.span_id);

      return {
        span,
        x: xPercent, // Position as percentage
        width: widthPercent, // Width as percentage
        startTime,
        endTime,
        isTooSmall,
        color,
        row: 0, // Will be assigned
      };
    });

    // Sort by start time
    spansWithPositions.sort((a, b) => a.startTime - b.startTime);

    // Assign rows to avoid overlaps
    const rows: Array<{ endTime: number }> = [];

    spansWithPositions.forEach((spanPos) => {
      // Find the first row where this span doesn't overlap
      let assignedRow = -1;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]!; // Safe because we're iterating within bounds
        if (spanPos.startTime >= row.endTime) {
          assignedRow = i;
          row.endTime = spanPos.endTime;
          break;
        }
      }

      // If no row found, create a new one
      if (assignedRow === -1) {
        assignedRow = rows.length;
        rows.push({ endTime: spanPos.endTime });
      }

      spanPos.row = assignedRow;
    });

    return {
      spanPositions: spansWithPositions,
      numRows: Math.max(1, rows.length),
    };
  }, [spans, timeRange, timeSpan]);

  const rowHeight = 20; // Height per row
  const totalHeight = numRows * rowHeight + 10; // Add padding
  const horizontalPadding = 12; // Padding on left and right to keep edge spans visible

  return (
    <Box
      sx={{
        position: "relative",
        height: totalHeight,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        px: `${horizontalPadding}px`,
      }}
    >
      {/* Time markers */}
      {Array.from({ length: 5 }, (_, i) => {
        const x = (i / 4) * 100;
        return (
          <Box
            key={i}
            sx={{
              position: "absolute",
              left: `${x}%`,
              top: 0,
              bottom: 0,
              width: 1,
              pointerEvents: "none",
            }}
          />
        );
      })}

      {/* Span bars/dots */}
      {spanPositions.map(({ span, x, width, isTooSmall, color, row }) => {
        const isHovered = hoveredSpanId === span.span_id;
        const opacity = isHovered ? 0.8 : hoveredSpanId ? 0.3 : 0.6;
        const zIndex = isHovered ? 5 : 1;
        const topOffset = row * rowHeight + 5; // 5px padding from top
        const barHeight = 12; // Fixed height for bars

        if (isTooSmall) {
          // Render as dot
          return (
            <Box
              key={span.span_id}
              onClick={() => onSpanClick(span.span_id)}
              onMouseEnter={() => onSpanHover(span.span_id)}
              onMouseLeave={() => onSpanHover(null)}
              sx={{
                position: "absolute",
                left: `${x}%`,
                top: topOffset + barHeight / 2,
                transform: "translate(-50%, -50%)",
                width: barHeight,
                height: barHeight,
                borderRadius: "50%",
                bgcolor: color,
                opacity,
                cursor: "pointer",
                zIndex,
                "&:hover": {
                  opacity: 1,
                  transform: "translate(-50%, -50%) scale(1.3)",
                },
              }}
            />
          );
        }

        // Render as bar
        return (
          <Box
            key={span.span_id}
            onClick={() => onSpanClick(span.span_id)}
            onMouseEnter={() => onSpanHover(span.span_id)}
            onMouseLeave={() => onSpanHover(null)}
            sx={{
              position: "absolute",
              left: `${x}%`,
              width: `${width}%`,
              top: topOffset,
              height: barHeight,
              bgcolor: color,
              opacity,
              cursor: "pointer",
              zIndex,
              borderRadius: 0.5,
              "&:hover": {
                opacity: 1,
                height: barHeight + 2,
                top: topOffset - 1,
              },
            }}
          />
        );
      })}
    </Box>
  );
}
